import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { 
  MercadoPagoCredentialsResponse, 
  isMercadoPagoConnected,
  MercadoPagoConnectedResponse 
} from '../api/types/mercadopago';
import { mercadoPagoApi } from '../api/mercadopago';

interface MercadoPagoContextType {
  isLoading: boolean;
  isConnected: boolean;
  connectionData: MercadoPagoConnectedResponse | null;
  error: string | null;
  refreshStatus: () => Promise<void>;
  checkConnectionStatus: () => Promise<void>;
  disconnectCredentials: () => Promise<void>;
  isDisconnecting: boolean;
}

const MercadoPagoContext = createContext<MercadoPagoContextType | undefined>(undefined);

interface MercadoPagoProviderProps {
  children: ReactNode;
}

export const MercadoPagoProvider: React.FC<MercadoPagoProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionData, setConnectionData] = useState<MercadoPagoConnectedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const checkConnectionStatus = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoading) {
      console.log('🔄 MercadoPago check already in progress, skipping...');
      return;
    }

    console.log('🔍 Starting MercadoPago connection check (Context)...');
    setIsLoading(true);
    setError(null);

    try {
      console.log('🔍 Checking MercadoPago connection status (Context)...');
      const response: MercadoPagoCredentialsResponse = await mercadoPagoApi.checkCredentials();
      console.log('📋 MercadoPago API response (Context):', JSON.stringify(response, null, 2));
      
      if (isMercadoPagoConnected(response)) {
        console.log('✅ MercadoPago connected (Context), updating shared state:', response);
        setIsConnected(true);
        setConnectionData(response);
      } else {
        console.log('⚠️ MercadoPago not connected (Context), updating shared state:', response);
        setIsConnected(false);
        setConnectionData(null);
      }
    } catch (err) {
      console.error('❌ Error checking MercadoPago connection (Context):', err);
      setError(err instanceof Error ? err.message : 'Error verificando conexión de MercadoPago');
      setIsConnected(false);
      setConnectionData(null);
    } finally {
      console.log('🏁 MercadoPago check completed (Context), setting isLoading to false');
      setIsLoading(false);
    }
  }, [isLoading]);

  const refreshStatus = useCallback(() => {
    return checkConnectionStatus();
  }, [checkConnectionStatus]);

  const disconnectCredentials = useCallback(async () => {
    if (isDisconnecting) {
      console.log('🔄 Disconnection already in progress, skipping...');
      return;
    }

    console.log('🔌 Starting MercadoPago credentials disconnection...');
    setIsDisconnecting(true);
    setError(null);

    try {
      const response = await mercadoPagoApi.deleteCredentials();
      console.log('✅ MercadoPago credentials disconnected successfully:', response);
      
      // Update state to reflect disconnection
      setIsConnected(false);
      setConnectionData(null);
      
    } catch (err) {
      console.error('❌ Error disconnecting MercadoPago credentials:', err);
      setError(err instanceof Error ? err.message : 'Error desconectando credenciales de MercadoPago');
    } finally {
      console.log('🏁 MercadoPago disconnection completed');
      setIsDisconnecting(false);
    }
  }, [isDisconnecting]);

  const value: MercadoPagoContextType = {
    isLoading,
    isConnected,
    connectionData,
    error,
    refreshStatus,
    checkConnectionStatus,
    disconnectCredentials,
    isDisconnecting,
  };

  return (
    <MercadoPagoContext.Provider value={value}>
      {children}
    </MercadoPagoContext.Provider>
  );
};

export const useMercadoPagoContext = (): MercadoPagoContextType => {
  const context = useContext(MercadoPagoContext);
  if (context === undefined) {
    throw new Error('useMercadoPagoContext must be used within a MercadoPagoProvider');
  }
  return context;
};
